import { useCallback, useState } from "react";
import { toast } from "sonner";

import { api } from "@/lib/api";

/**
 * Local Adsum (Agent 3) state hook.
 *
 * Owned by SmartPublishWorkspace so the orb + drawer share the same
 * review/loading/error state. Drawer can be closed and reopened and the
 * last result is preserved. Reset is called explicitly on new asset.
 */
export default function useAdsumState() {
    const [open, setOpen] = useState(false);
    // 'idle' | 'loading' | 'result' | 'error' | 'insufficient'
    const [phase, setPhase] = useState("idle");
    const [review, setReview] = useState(null);
    const [error, setError] = useState(null);
    const [applied, setApplied] = useState(false);

    const toggle = useCallback(() => setOpen((o) => !o), []);

    const resetForNewAsset = useCallback(() => {
        setPhase("idle");
        setReview(null);
        setError(null);
        setApplied(false);
    }, []);

    const runReview = useCallback(
        async (payload, { setBalanceFast, openVault } = {}) => {
            setPhase("loading");
            setError(null);
            setApplied(false);
            try {
                const { data } = await api.post(
                    "/agent3/adsum/review",
                    payload
                );
                setReview(data);
                if (typeof data.balance_after === "number") {
                    setBalanceFast?.(data.balance_after);
                }
                setPhase("result");
                toast.success("Adsum audit complete", {
                    description: `Brand score ${data.brand_score}/100`,
                });
            } catch (err) {
                const status = err?.response?.status;
                if (status === 402) {
                    setPhase("insufficient");
                    openVault?.("topup");
                    return;
                }
                const detail = err?.response?.data?.detail;
                const msg =
                    typeof detail === "string"
                        ? detail
                        : detail?.error
                          ? `${detail.field || "request"}: ${detail.error}`
                          : err?.message || "Adsum failed";
                setError(msg);
                setPhase("error");
                toast.error("Adsum couldn't complete the review", {
                    description: msg,
                });
            }
        },
        []
    );

    const markApplied = useCallback(() => setApplied(true), []);

    return {
        open,
        setOpen,
        toggle,
        phase,
        review,
        error,
        applied,
        runReview,
        markApplied,
        resetForNewAsset,
    };
}
